import { Switch } from 'antd';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationContext } from '@client/context/NotificationContext';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useMarketplaceQueries } from '@client/queries/useMarketplaceQueries';

type TFeatureToggleProps = {
  id: string;
  featured: boolean;
};

/**
 * Admin-only switch toggling the `featured` flag on a marketplace user
 * subscription. Calls `PATCH /admin/marketplace/:id/feature` and shows
 * a notification on error, reverting the optimistic state.
 * @param {TFeatureToggleProps} props - Props
 * @param {string} props.id - User subscription id
 * @param {boolean} props.featured - Current featured value
 * @returns {React.JSX.Element} The rendered switch
 */
export const FeatureToggle: React.FC<TFeatureToggleProps> = ({
  id,
  featured,
}: TFeatureToggleProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { ability } = useAuth();
  const { notificationApi } = useContext(NotificationContext);
  const { useSetFeatured } = useMarketplaceQueries();
  const { mutate, isPending } = useSetFeatured();

  const [optimistic, setOptimistic] = useState<boolean>(featured);

  // Sync from props when refetched data changes the underlying row.
  React.useEffect(() => {
    setOptimistic(featured);
  }, [featured]);

  const disabled = ability.cannot(
    EAbilityActions.MANAGE,
    EAbilitySubjects.MARKETPLACE,
  );

  const onChange = (next: boolean): void => {
    const previous = optimistic;
    setOptimistic(next);
    mutate(
      { id, featured: next },
      {
        onSuccess: () => {
          notificationApi?.success({
            message: t('admin.marketplace.toggle.success'),
          });
        },
        onError: () => {
          setOptimistic(previous);
          notificationApi?.error({
            message: t('admin.marketplace.toggle.error'),
          });
        },
      },
    );
  };

  return (
    <Switch
      checked={optimistic}
      loading={isPending}
      disabled={disabled}
      onChange={onChange}
    />
  );
};
